import { useState, useEffect, useMemo } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../../api/staff'
import type { AuthSession } from '../../../../types/plans'
import {
  getStageName,
  getStageSortOrder,
  getServices,
  getServiceQty,
  parseDecimalInput,
  formatPlainNumber,
  toDateInputValue,
  addMonthsToDate,
  createMonthlyInstallmentRows,
  redistributeInstallmentRows,
} from '../constructorTypes'
import type { ConstructorPlan, InstallmentPaymentDraft } from '../constructorTypes'

export function InstallmentPanel({
  plan,
  bootstrap,
  session,
  onReload,
}: {
  plan: ConstructorPlan
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onReload: () => void
}) {
  const stages = useMemo(
    () => (plan.stages ?? []).slice().sort((a, b) => getStageSortOrder(a) - getStageSortOrder(b)),
    [plan.stages],
  )
  const totalAmount = stages.flatMap(getServices).reduce((sum, svc) => {
    const unit = svc.unit_price ?? svc.price ?? 0
    const qty = getServiceQty(svc)
    return sum + (svc.total_price ?? unit * qty)
  }, 0)
  const installment = plan.installment ?? null
  const [enabled, setEnabled] = useState(Boolean(installment?.enabled))
  const [months, setMonths] = useState(formatPlainNumber(installment?.months ?? null))
  const [amount, setAmount] = useState(formatPlainNumber(installment?.amount ?? (totalAmount || null)))
  const [rate, setRate] = useState(formatPlainNumber(installment?.rate ?? null))
  const [startDate, setStartDate] = useState(toDateInputValue(installment?.start_date ?? null))
  const [note, setNote] = useState(installment?.note ?? '')
  const [rows, setRows] = useState<InstallmentPaymentDraft[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const regenerateMonthlyRows = (nextAmount = amount, nextMonths = months, nextStartDate = startDate) => {
    const parsedAmount = parseDecimalInput(nextAmount)
    const parsedMonths = parseDecimalInput(nextMonths)
    setRows(createMonthlyInstallmentRows(parsedAmount, parsedMonths, nextStartDate))
  }

  useEffect(() => {
    const next = plan.installment ?? null
    setEnabled(Boolean(next?.enabled))
    setMonths(formatPlainNumber(next?.months ?? null))
    setAmount(formatPlainNumber(next?.amount ?? (totalAmount || null)))
    setRate(formatPlainNumber(next?.rate ?? null))
    setStartDate(toDateInputValue(next?.start_date ?? null))
    setNote(next?.note ?? '')
    const nextSchedule = next?.schedule ?? []
    const nextMonths = next?.months ?? null
    const nextAmount = next?.amount ?? (totalAmount || null)
    if (next?.enabled && nextMonths && nextAmount && (nextSchedule.length !== nextMonths || nextSchedule.some((payment) => payment.amount == null))) {
      setRows(createMonthlyInstallmentRows(nextAmount, nextMonths, toDateInputValue(next.start_date ?? null)))
      return
    }
    setRows(nextSchedule.map((payment, index) => ({
      key: `payment-${payment.id ?? index}`,
      id: payment.id ?? null,
      stage_id: payment.stage_id ? String(payment.stage_id) : '',
      stage_title: payment.stage_title ?? null,
      title: payment.title,
      amount: formatPlainNumber(payment.amount ?? null),
      due_date: toDateInputValue(payment.due_date ?? null),
      sort_order: payment.sort_order ?? index,
      status: payment.status ?? 'planned',
      note: payment.note ?? '',
    })))
  }, [plan.installment, totalAmount])

  function handleToggle(value: boolean) {
    setEnabled(value)
    if (value && rows.length === 0) {
      const nextAmount = amount === '' && totalAmount > 0 ? String(Math.round(totalAmount)) : amount
      const nextMonths = months === '' ? '12' : months
      const nextStartDate = startDate || new Date().toISOString().slice(0, 10)
      if (amount !== nextAmount) setAmount(nextAmount)
      if (months !== nextMonths) setMonths(nextMonths)
      if (startDate !== nextStartDate) setStartDate(nextStartDate)
      setRows(createMonthlyInstallmentRows(parseDecimalInput(nextAmount), parseDecimalInput(nextMonths), nextStartDate))
    }
  }

  function updateRow(key: string, patch: Partial<InstallmentPaymentDraft>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }

  function updateRowsForAmount(nextAmount: string) {
    setAmount(nextAmount)
    if (enabled) regenerateMonthlyRows(nextAmount, months, startDate)
  }

  function updateRowsForMonths(nextMonths: string) {
    setMonths(nextMonths)
    if (enabled) regenerateMonthlyRows(amount, nextMonths, startDate)
  }

  function updateRowsForStartDate(nextStartDate: string) {
    setStartDate(nextStartDate)
    if (enabled) {
      setRows((current) => current.map((row, index) => ({
        ...row,
        due_date: nextStartDate ? addMonthsToDate(nextStartDate, index) : '',
      })))
    }
  }

  function updateRowAmount(key: string, value: string) {
    setRows((current) => {
      const changedIndex = current.findIndex((row) => row.key === key)
      const nextRows = current.map((row) => (row.key === key ? { ...row, amount: value } : row))
      return redistributeInstallmentRows(nextRows, parseDecimalInput(amount), changedIndex)
    })
  }

  function handleStageChange(row: InstallmentPaymentDraft, stageId: string) {
    const stage = stages.find((candidate) => String(candidate.id) === stageId)
    updateRow(row.key, {
      stage_id: stageId,
      stage_title: stage ? getStageName(stage) : null,
      title: row.title.trim() === '' || row.title === row.stage_title ? (stage ? getStageName(stage) : row.title) : row.title,
    })
  }

  function addRow() {
    setRows((current) => [
      ...current,
      {
        key: `new-${Date.now()}-${current.length}`,
        stage_id: '',
        stage_title: null,
        title: `Платёж ${current.length + 1}`,
        amount: '',
        due_date: startDate ? addMonthsToDate(startDate, current.length) : '',
        sort_order: current.length,
        status: 'planned',
        note: '',
      },
    ])
  }

  async function handleSave() {
    setSaving(true)
    setError('')
    try {
      await staffApi.updateConstructorPlan(bootstrap, session, plan.id, {
        installment: {
          enabled,
          apply_to: 'plan',
          months: parseDecimalInput(months),
          amount: parseDecimalInput(amount),
          rate: parseDecimalInput(rate),
          start_date: startDate || null,
          note: note.trim() || null,
          schedule: enabled ? rows.map((row, index) => ({
            id: row.id ?? null,
            stage_id: row.stage_id ? Number(row.stage_id) : null,
            stage_title: row.stage_title ?? null,
            title: row.title.trim() || `Платёж ${index + 1}`,
            amount: parseDecimalInput(row.amount),
            due_date: row.due_date || null,
            sort_order: index,
            status: row.status || 'planned',
            note: row.note.trim() || null,
          })) : [],
        },
      })
      onReload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить рассрочку')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Рассрочка</h3>
          <p className="text-[11px] text-slate-500">График будет показан пациенту в публичном плане.</p>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            className="rounded border-slate-300"
            checked={enabled}
            onChange={(event) => handleToggle(event.target.checked)}
            disabled={saving}
          />
          Включена
        </label>
      </div>

      <div className={`grid grid-cols-2 gap-2 text-xs transition-opacity ${!enabled ? 'pointer-events-none opacity-40' : ''}`}>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Сумма, ₽</span>
          <input
            className="rounded border border-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
            value={amount}
            onChange={(event) => updateRowsForAmount(event.target.value)}
            inputMode="decimal"
            disabled={saving || !enabled}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Срок, мес.</span>
          <input
            className="rounded border border-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
            value={months}
            onChange={(event) => updateRowsForMonths(event.target.value)}
            inputMode="numeric"
            disabled={saving || !enabled}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Старт</span>
          <input
            type="date"
            className="rounded border border-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
            value={startDate}
            onChange={(event) => updateRowsForStartDate(event.target.value)}
            disabled={saving || !enabled}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Ставка, %</span>
          <input
            className="rounded border border-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
            inputMode="decimal"
            disabled={saving || !enabled}
          />
        </label>
      </div>

      <label className={`mt-2 flex flex-col gap-1 text-xs transition-opacity ${!enabled ? 'pointer-events-none opacity-40' : ''}`}>
        <span className="text-[10px] uppercase tracking-wide text-slate-500">Комментарий</span>
        <textarea
          className="min-h-16 rounded border border-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          disabled={saving || !enabled}
          placeholder="Условия рассрочки для пациента"
        />
      </label>

      <div className={`mt-3 space-y-2 transition-opacity ${!enabled ? 'pointer-events-none opacity-40' : ''}`}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-700">График платежей</span>
          <button
            type="button"
            className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            onClick={addRow}
            disabled={saving || !enabled}
          >
            + Платёж
          </button>
        </div>
        {rows.length === 0 ? (
          <button
            type="button"
            className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-50"
            onClick={() => regenerateMonthlyRows()}
            disabled={saving || !enabled}
          >
            Сформировать помесячно
          </button>
        ) : (
          <div className="max-h-[430px] space-y-2 overflow-y-auto pr-1">
          {rows.map((row, index) => (
            <div key={row.key} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold text-slate-600">#{index + 1}</span>
                <button
                  type="button"
                  className="text-[11px] text-red-500 hover:text-red-700 disabled:opacity-50"
                  onClick={() => setRows((current) => current.filter((candidate) => candidate.key !== row.key))}
                  disabled={saving || !enabled}
                >
                  Удалить
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <select
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
                  value={row.stage_id}
                  onChange={(event) => handleStageChange(row, event.target.value)}
                  disabled={saving || !enabled}
                >
                  <option value="">Без этапа</option>
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>{getStageName(stage)}</option>
                  ))}
                </select>
                <input
                  className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
                  value={row.title}
                  onChange={(event) => updateRow(row.key, { title: event.target.value })}
                  placeholder="Название платежа"
                  disabled={saving || !enabled}
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
                    value={row.amount}
                    onChange={(event) => updateRowAmount(row.key, event.target.value)}
                    placeholder="Сумма"
                    inputMode="decimal"
                    disabled={saving || !enabled}
                  />
                  <input
                    type="date"
                    className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
                    value={row.due_date}
                    onChange={(event) => updateRow(row.key, { due_date: event.target.value })}
                    disabled={saving || !enabled}
                  />
                </div>
                <input
                  className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-100"
                  value={row.note}
                  onChange={(event) => updateRow(row.key, { note: event.target.value })}
                  placeholder="Комментарий к платежу"
                  disabled={saving || !enabled}
                />
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      <button
        type="button"
        className="mt-3 w-full rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        onClick={() => void handleSave()}
        disabled={saving}
      >
        {saving ? 'Сохраняем…' : 'Сохранить рассрочку'}
      </button>
    </div>
  )
}
